import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { toast } from 'sonner';
import { getStorageItem, setStorageItem } from '@utils/helpers';
import type { Photo } from '@/types';

interface FavoriteItem {
  photoId: string;
  eventId: string;
  thumbnail: string;
  title: string;
  addedAt: string;
}

interface FavoritesState {
  items: FavoriteItem[];
  loading: boolean;
}

type FavoritesAction =
  | { type: 'ADD_FAVORITE'; payload: FavoriteItem }
  | { type: 'REMOVE_FAVORITE'; payload: string }
  | { type: 'CLEAR_FAVORITES' }
  | { type: 'LOAD_FAVORITES'; payload: FavoriteItem[] }
  | { type: 'SET_LOADING'; payload: boolean };

interface FavoritesContextType {
  favorites: FavoriteItem[];
  favoritesCount: number;
  loading: boolean;
  addToFavorites: (photo: Photo) => void;
  removeFromFavorites: (photoId: string) => void;
  isFavorite: (photoId: string) => boolean;
  toggleFavorite: (photo: Photo) => void;
  clearFavorites: () => void;
}

const initialState: FavoritesState = {
  items: [],
  loading: false,
};

const favoritesReducer = (state: FavoritesState, action: FavoritesAction): FavoritesState => {
  switch (action.type) {
    case 'ADD_FAVORITE': {
      const exists = state.items.some(item => item.photoId === action.payload.photoId);
      if (exists) return state;

      return {
        ...state,
        items: [...state.items, action.payload],
      };
    }

    case 'REMOVE_FAVORITE':
      return {
        ...state,
        items: state.items.filter(item => item.photoId !== action.payload),
      };

    case 'CLEAR_FAVORITES':
      return {
        ...state,
        items: [],
      };

    case 'LOAD_FAVORITES':
      return {
        ...state,
        items: action.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };

    default:
      return state;
  }
};

const FavoritesContext = createContext<FavoritesContextType | null>(null);

interface FavoritesProviderProps {
  children: ReactNode;
}

// Add FAVORITES_DATA to storage keys if not present
const FAVORITES_STORAGE_KEY = 'pixelshare_favorites';

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(favoritesReducer, initialState);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = getStorageItem(FAVORITES_STORAGE_KEY, []);
    if (savedFavorites && Array.isArray(savedFavorites)) {
      dispatch({ type: 'LOAD_FAVORITES', payload: savedFavorites });
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    setStorageItem(FAVORITES_STORAGE_KEY, state.items);
  }, [state.items]);

  const addToFavorites = (photo: Photo): void => {
    const favoriteItem: FavoriteItem = {
      photoId: photo.id,
      eventId: photo.eventId,
      thumbnail: photo.thumbnail,
      title: photo.title || `Photo ${photo.id}`,
      addedAt: new Date().toISOString(),
    };
    dispatch({ type: 'ADD_FAVORITE', payload: favoriteItem });
    toast.success('Added to favorites');
  };

  const removeFromFavorites = (photoId: string): void => {
    dispatch({ type: 'REMOVE_FAVORITE', payload: photoId });
    toast.success('Removed from favorites');
  };

  const isFavorite = (photoId: string): boolean => {
    return state.items.some(item => item.photoId === photoId);
  };

  const toggleFavorite = (photo: Photo): void => {
    if (isFavorite(photo.id)) {
      removeFromFavorites(photo.id);
    } else {
      addToFavorites(photo);
    }
  };

  const clearFavorites = (): void => {
    dispatch({ type: 'CLEAR_FAVORITES' });
    toast.success('Favorites cleared');
  };

  const value: FavoritesContextType = {
    favorites: state.items,
    favoritesCount: state.items.length,
    loading: state.loading,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    toggleFavorite,
    clearFavorites,
  };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
};
